/**
 * StylusKeyboardManager - Facade-facing API for the stylus-keyboard text mode.
 *
 * Thin wrapper over StylusKeyboardDragger so UI code never touches the
 * dragger directly (same pattern as TextManager/HandwritingManager).
 */

import type { StylusKeyboardDragger } from "../draggers/StylusKeyboardDragger";

export interface StylusKeyboardManagerDeps {
  stylusKeyboardDragger: StylusKeyboardDragger;
}

export class StylusKeyboardManager {
  private dragger: StylusKeyboardDragger;

  constructor(deps: StylusKeyboardManagerDeps) {
    this.dragger = deps.stylusKeyboardDragger;
  }

  /**
   * Subscribe to placement requests: the user tapped the drawing and the
   * text input should open. Returns an unsubscribe function.
   */
  onPlacementRequest(listener: () => void): () => void {
    return this.dragger.onPlacementRequest(listener);
  }

  /** Whether a position is waiting for its text. */
  get hasPendingPlacement(): boolean {
    return this.dragger.hasPendingPlacement();
  }

  /** Insert the validated text at the pending position. */
  insertText(text: string): void {
    this.dragger.insertText(text);
  }

  /** Forget the pending position (input cancelled). */
  cancelPlacement(): void {
    this.dragger.cancelPlacement();
  }
}
